import { useEffect, useState } from 'react';
import { Paginator } from '@/shared/ui/Paginator/Paginator';
import { Sidebar } from 'primereact/sidebar';
import type { ApiSettingType, AuditHistoryResponse } from '@/core/generated/api';
import { auditService } from '@/entities/Audit/api/auditService';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import classes from './AuditHistoryButton.module.scss';

interface AuditHistoryDrawerProps {
    visible: boolean;
    onHide: () => void;
    productId?: string;
    settingType?: ApiSettingType;
}

/** Открывает историю изменения полей продукта или раздела настроек */
export const AuditHistoryDrawer = (
    { productId, settingType, visible, onHide }: AuditHistoryDrawerProps
) => {
    const [page, setPage] = useState(1);
    const [requestedPageSize, setRequestedPageSize] = useState(20);
    const [history, setHistory] = useState<AuditHistoryResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [retryKey, setRetryKey] = useState(0);

    useEffect(() => {
        if (!visible) return;

        const controller = new AbortController();

        const request = productId
            ? auditService.getProductHistory(productId, page, controller.signal, requestedPageSize)
            : settingType
                ? auditService.getVoxSettingsHistory(settingType, page, controller.signal, requestedPageSize)
                : null;

        if (!request) return;

        setLoading(true);
        setError(null);

        void request.then((response) => {
            if (!controller.signal.aborted) setHistory(response);
        })
            .catch((reason: unknown) => {
                if (!controller.signal.aborted) {
                    setError(reason instanceof Error
                        ? reason.message
                        : 'Не удалось загрузить историю');
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [visible, productId, settingType, page, requestedPageSize, retryKey]);

    const entries = history?.groups?.flatMap((group) => group.entries ?? []) ?? [];
    const pageSize = history?.pageSize ?? requestedPageSize;
    const totalCount = history?.totalCount ?? 0;

    return <Sidebar
        visible={visible}
        onHide={onHide}
        position="right"
        header="История изменений"
        className={classes.drawer}
    >
        <p className={classes.description}>
            Изменения полей: кто, когда и какое значение установил
        </p>
        {loading &&
            <RequestLoading message="Загрузка истории..." />
        }
        {!loading && error &&
            <RequestError
                message={error}
                onRetry={() => setRetryKey((value) => value + 1)}
            />
        }
        {!loading && !error && entries.length === 0 &&
            <RequestEmpty message="Изменений пока нет" />
        }
        {!loading && !error && entries.length > 0 &&
            <ol className={classes.entries}>
                {entries.map((entry) => <li
                    key={entry.id}
                    className={classes.entry}
                >
                    <div className={classes.meta}>
                        <strong>
                            {entry.userName || 'Система'}
                        </strong>
                        <time dateTime={entry.created}>
                            {entry.created && new Intl.DateTimeFormat('ru-RU', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                            }).format(new Date(entry.created))}
                        </time>
                    </div>
                    {entry.subject &&
                        <div className={classes.subject}>
                            {entry.subject}
                        </div>
                    }
                    <ul className={classes.changes}>
                        {(entry.changesList ?? []).map((change, index) =>
                            <li
                                key={`${change.field}-${index}`}
                                className={classes.change}
                            >
                                <strong>
                                    {change.field}
                                </strong>
                                <div className={classes.values}>
                                    {(change.oldValue !== undefined
                                        || change.newValue !== undefined) &&
                                        <>
                                            <span>
                                                {change.oldValue ?? '—'}
                                            </span>
                                            <span aria-hidden="true">→</span>
                                            <span>
                                                {change.newValue ?? '—'}
                                            </span>
                                        </>
                                    }
                                    {(change.added?.length ?? 0) > 0 &&
                                        <span>Добавлено: {change.added?.join(', ')}
                                        </span>
                                    }
                                    {(change.removed?.length ?? 0) > 0 &&
                                        <span>Удалено: {change.removed?.join(', ')}
                                        </span>
                                    }
                                </div>
                            </li>)}
                    </ul>
                </li>)}
            </ol>
        }
        {history &&
            <Paginator
                label="Страницы истории"
                first={(page - 1) * pageSize}
                rows={pageSize}
                totalRecords={totalCount}
                rowsPerPageOptions={[10, 20, 50, 100]}
                disabled={loading || Boolean(error)}
                onPageChange={event => {
                    setPage(event.page + 1);
                    setRequestedPageSize(event.rows);
                }}
            />
        }
    </Sidebar>;
};
